/**
 * The WIDGET's own locale vocabulary — fifty languages, and deliberately NOT
 * `@axon/i18n`'s `SupportedLocale`.
 *
 * ── WHY A SECOND VOCABULARY, WHEN ONE ALREADY EXISTS ──────────────────────
 *
 * `packages/i18n` declares `LOCALES = ['uk', 'en']`, and that list is load-
 * bearing for the CONSOLE: `catalogs.test.ts` does `describe.each(LOCALES)` and
 * demands a `.po` catalogue translating every `ALL_MESSAGE_IDS` for each one.
 * Adding forty-eight entries there would therefore demand forty-eight console
 * catalogues — hundreds of agent-facing strings nobody asked to translate — to
 * ship copy that only a visitor ever reads.
 *
 * The two audiences are genuinely different, which is the same reason
 * `apps/widget/src/i18n/locale.ts` exists rather than reusing the console's
 * resolver. The console is read by the tenant's own staff, who were hired into a
 * language. The widget is read by MEMBERS OF THE PUBLIC on somebody else's
 * website, arriving from anywhere. A vocabulary that serves both is a vocabulary
 * that is wrong for one of them.
 *
 * ── A LOCALE IS A CATALOGUE THAT EXISTS ───────────────────────────────────
 *
 * Every tag below has a file at `./catalogs/<tag>.ts`, and
 * `locales.test.ts` asserts the list and the directory agree in BOTH directions.
 * That is what keeps "add a language" an honest one-file operation: a tag with no
 * catalogue would resolve, load nothing and render blank labels at a visitor,
 * and a catalogue with no tag is dead weight in the build.
 *
 * ── THE FALLBACK IS ENGLISH HERE AND UKRAINIAN THERE, ON PURPOSE ──────────
 *
 * `@axon/i18n`'s `DEFAULT_LOCALE` is `uk`, and its own comment gives the reason:
 * "the product's first market is Ukraine and an unknown visitor is far more
 * likely to read Ukrainian than English". That reasoning is about the CONSOLE's
 * user, who works for a Ukrainian tenant. It inverts for this audience: a
 * visitor whose browser asked for German, Polish or Japanese and got Ukrainian
 * has been handed a language they almost certainly cannot read, when English was
 * available. So the widget falls back to English and the console keeps falling
 * back to Ukrainian, and neither is a default the other has to live with.
 */

/**
 * The fifty, ordered by tag so the list is diffable and the switcher is stable.
 *
 * Region subtags appear only where the SCRIPT differs and a bare language tag
 * would be ambiguous: `zh-Hans` and `zh-Hant` are not interchangeable, and a
 * reader of one does not simply read the other. Everywhere else the bare
 * language subtag is used, because `resolveWidgetLocale` matches on the primary
 * subtag — a visitor whose browser says `pt-BR` or `de-AT` must match `pt` and
 * `de` rather than falling through to English.
 */
export const WIDGET_LOCALES = [
  'ar',
  'az',
  'bg',
  'bn',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'es',
  'et',
  'fa',
  'fi',
  'fil',
  'fr',
  'gu',
  'he',
  'hi',
  'hr',
  'hu',
  'id',
  'it',
  'ja',
  'kk',
  'ko',
  'lt',
  'lv',
  'ml',
  'mr',
  'ms',
  'nb',
  'nl',
  'pl',
  'pt',
  'ro',
  'ru',
  'sk',
  'sl',
  'sr',
  'sv',
  'sw',
  'ta',
  'te',
  'th',
  'tr',
  'uk',
  'ur',
  'vi',
  'zh-Hans',
  'zh-Hant',
] as const;

export type WidgetLocale = (typeof WIDGET_LOCALES)[number];

/**
 * Where an unrecognised visitor lands. See the header for why this is English
 * while the console's is Ukrainian.
 *
 * It is also the ONLY catalogue compiled into the main bundle — see
 * `messages.ts`. Every other language is a chunk fetched when it is the one
 * actually needed, so a panel always has something to render immediately.
 */
export const WIDGET_DEFAULT_LOCALE: WidgetLocale = 'en';

/** Narrow an arbitrary string to a locale this widget has copy for. */
export function isWidgetLocale(value: string): value is WidgetLocale {
  return (WIDGET_LOCALES as readonly string[]).includes(value);
}

/**
 * Tags real browsers send that the primary-subtag rule would get wrong.
 *
 * `@axon/i18n`'s `normalizeLocale` reduces a tag to everything before the first
 * `-` and looks that up, which is correct for forty-six of these fifty and
 * WRONG for the rest. Each entry below is a tag a device actually emits:
 *
 *   CHINESE IS THE ONE THAT MATTERS. `zh` alone is not a language this widget
 *   can render — the two catalogues are Simplified and Traditional, and a reader
 *   of one does not simply read the other. Browsers send `zh-CN`, `zh-TW`,
 *   `zh-HK` and `zh-Hans-CN` far more often than a bare script tag, so the
 *   region decides the script: mainland and Singapore are Simplified, Taiwan,
 *   Hong Kong and Macau are Traditional. Bare `zh` goes to Simplified, which is
 *   the larger population by a wide margin and the less surprising guess.
 *
 *   NORWEGIAN. `no` is the macrolanguage and `nn` is Nynorsk; both are mapped to
 *   Bokmål because that is the catalogue that exists and a Nynorsk reader reads
 *   Bokmål without difficulty. Pretending otherwise would drop them to English.
 *
 *   TAGALOG. `tl` is what older Android and iOS builds send for what CLDR now
 *   calls `fil`.
 *
 *   THE TWO ISO RENAMES. `iw`→`he` and `in`→`id` were changed in 1989 and Java —
 *   and therefore a long tail of Android WebViews — still emits the old ones.
 *   This is the same class of defect as the `uk_UA` underscore that
 *   `locale.ts` already repairs, and it fails the same silent way: a Hebrew
 *   visitor greeted in English.
 */
const TAG_ALIASES: Readonly<Record<string, WidgetLocale>> = Object.freeze({
  zh: 'zh-Hans',
  'zh-cn': 'zh-Hans',
  'zh-sg': 'zh-Hans',
  'zh-hans': 'zh-Hans',
  'zh-tw': 'zh-Hant',
  'zh-hk': 'zh-Hant',
  'zh-mo': 'zh-Hant',
  'zh-hant': 'zh-Hant',
  no: 'nb',
  nn: 'nb',
  tl: 'fil',
  iw: 'he',
  in: 'id',
});

/**
 * A tag from a device, a query string or a console field, reduced to a locale
 * this widget has a catalogue for — or `null`.
 *
 * `null` means "this tag said nothing", NOT "use the fallback". The caller walks
 * the visitor's whole preference list, and an entry that says nothing must let
 * the next entry speak: a visitor whose browser lists `de` then `uk` must get
 * Ukrainian rather than English, and that only works if `de`'s failure is
 * distinguishable from the end of the list.
 */
export function normalizeWidgetLocale(tag: string): WidgetLocale | null {
  // `_` for `-` is the Android/Java spelling; `locale.ts` repairs it on the way
  // in and it is repaired here too, because this function is also called on
  // strings a human typed into the console.
  const cleaned = tag.trim().replaceAll('_', '-').toLowerCase();
  if (cleaned === '') return null;

  // Longest match first: `zh-hans-cn` must find `zh-hans`, not `zh`.
  const parts = cleaned.split('-');
  for (let take = parts.length; take > 0; take -= 1) {
    const candidate = parts.slice(0, take).join('-');
    const alias = TAG_ALIASES[candidate];
    if (alias !== undefined) return alias;
    // The catalogue tags are cased (`zh-Hans`), the incoming tag is not, so the
    // comparison is done on a lowered copy of the vocabulary rather than by
    // hoping the caller sent the right case.
    const direct = WIDGET_LOCALES.find((locale) => locale.toLowerCase() === candidate);
    if (direct !== undefined) return direct;
  }
  return null;
}

/**
 * BCP-47 tags for `Intl`, which is a different question from "which catalogue".
 *
 * A REGION IS PINNED ONLY WHERE LEAVING IT OUT CHANGES THE OUTPUT. `Intl` will
 * happily resolve a bare `de`, and for most of these the bare subtag already
 * produces the right date order and decimal separator. The entries that carry a
 * region carry it because the default resolution is wrong or ambiguous for the
 * audience: `pt-BR` over European Portuguese (the larger reader population by an
 * order of magnitude), `zh-Hans-CN`/`zh-Hant-TW` because script alone leaves the
 * calendar and numbering unresolved, and `en-GB` because this widget's other
 * forty-nine languages all render day-before-month and an American visitor
 * reading `14/09` is less confused than a European reading `09/14`.
 */
export const WIDGET_LOCALE_BCP47: Readonly<Record<WidgetLocale, string>> = Object.freeze({
  ar: 'ar',
  az: 'az',
  bg: 'bg',
  bn: 'bn',
  cs: 'cs',
  da: 'da',
  de: 'de',
  el: 'el',
  en: 'en-GB',
  es: 'es',
  et: 'et',
  fa: 'fa',
  fi: 'fi',
  fil: 'fil',
  fr: 'fr',
  gu: 'gu',
  he: 'he',
  hi: 'hi',
  hr: 'hr',
  hu: 'hu',
  id: 'id',
  it: 'it',
  ja: 'ja',
  kk: 'kk',
  ko: 'ko',
  lt: 'lt',
  lv: 'lv',
  ml: 'ml',
  mr: 'mr',
  ms: 'ms',
  nb: 'nb',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt-BR',
  ro: 'ro',
  ru: 'ru',
  sk: 'sk',
  sl: 'sl',
  sr: 'sr',
  sv: 'sv',
  sw: 'sw',
  ta: 'ta',
  te: 'te',
  th: 'th',
  tr: 'tr',
  uk: 'uk-UA',
  ur: 'ur',
  vi: 'vi',
  'zh-Hans': 'zh-Hans-CN',
  'zh-Hant': 'zh-Hant-TW',
});

/**
 * Endonyms — each language written in itself, never translated.
 *
 * The rule is `@axon/i18n`'s and it matters more here, not less: the switcher is
 * shown to somebody who is currently reading a language they may not know, so
 * "Deutsch" must never render as "German" or "Німецька". A visitor scans this
 * list for the shape of their own language, and a translated list is a list they
 * cannot scan.
 */
export const WIDGET_LOCALE_ENDONYMS: Readonly<Record<WidgetLocale, string>> = Object.freeze({
  ar: 'العربية',
  az: 'Azərbaycan',
  bg: 'Български',
  bn: 'বাংলা',
  cs: 'Čeština',
  da: 'Dansk',
  de: 'Deutsch',
  el: 'Ελληνικά',
  en: 'English',
  es: 'Español',
  et: 'Eesti',
  fa: 'فارسی',
  fi: 'Suomi',
  fil: 'Filipino',
  fr: 'Français',
  gu: 'ગુજરાતી',
  he: 'עברית',
  hi: 'हिन्दी',
  hr: 'Hrvatski',
  hu: 'Magyar',
  id: 'Bahasa Indonesia',
  it: 'Italiano',
  ja: '日本語',
  kk: 'Қазақша',
  ko: '한국어',
  lt: 'Lietuvių',
  lv: 'Latviešu',
  ml: 'മലയാളം',
  mr: 'मराठी',
  ms: 'Bahasa Melayu',
  nb: 'Norsk bokmål',
  nl: 'Nederlands',
  pl: 'Polski',
  pt: 'Português',
  ro: 'Română',
  ru: 'Русский',
  sk: 'Slovenčina',
  sl: 'Slovenščina',
  sr: 'Српски',
  sv: 'Svenska',
  sw: 'Kiswahili',
  ta: 'தமிழ்',
  te: 'తెలుగు',
  th: 'ไทย',
  tr: 'Türkçe',
  uk: 'Українська',
  ur: 'اردو',
  vi: 'Tiếng Việt',
  'zh-Hans': '简体中文',
  'zh-Hant': '繁體中文',
});

/**
 * The languages a browser renders right-to-left, as a set rather than a flag on
 * each entry.
 *
 * The panel sets `dir` from this. Getting it wrong is not cosmetic: an
 * Arabic or Hebrew transcript laid out left-to-right puts the composer, the
 * close button and the message gutter on the wrong side of a panel that is
 * already floating over somebody else's page, and punctuation lands at the wrong
 * end of every sentence.
 */
export const WIDGET_RTL_LOCALES: ReadonlySet<WidgetLocale> = new Set<WidgetLocale>([
  'ar',
  'fa',
  'he',
  'ur',
]);

/** `dir` for the panel root. */
export function widgetLocaleDirection(locale: WidgetLocale): 'ltr' | 'rtl' {
  return WIDGET_RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}
